import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Collection,
  Transaction as TransactionModel,
  TransactionDirection,
  TransactionProject,
} from 'api-client';
import {
  AccountNotReadyWarningSection,
  Container,
  DataTable,
  Pagination,
  Section,
  TransactionsPartialFilter,
} from 'components';
import { TransactionMobileCard } from 'components/pages/transactions/TransactionMobileCard';
import { Filter } from 'components/pages/transactions/TransactionsPartialFilter/TransactionsPartialFilter';
import { TransactionTableRow } from 'components/pages/transactions/TransactionTableRow';
import { createTransactionProjectProvider, createTransactionProvider } from 'di';
import { useErrorCallback, useTransactionsTranslations } from 'hooks';
import { DataCollection } from 'utils';

import './Transactions.scoped.scss';

const allProjectsOption: TransactionProject = {
  code: '-',
  uuid: '-',
  projectName: 'Проекты',
};

const TABLE_COLUMNS = '1.7fr 110px 1.2fr 1.7fr 1.1fr 2.2fr';

const Transactions = () => {
  const errorCallback = useErrorCallback();
  const [projects, setProjects] = useState<DataCollection<TransactionProject>>(
    new DataCollection()
  );
  const [loading, setLoading] = useState(false);
  const transactionsTranslations = useTransactionsTranslations();
  const [transactions, setTransactions] = useState<DataCollection<TransactionModel>>(
    new DataCollection()
  );
  const handleError = useErrorCallback();
  const [searchParams] = useSearchParams();
  const initialProjectUuids = searchParams.getAll('filter[projectUuids][]');
  const [filter, setFilter] = useState<Filter>(() => ({
    search: '',
    dateFrom: '',
    dateTo: '',
    transactionDirection: null,
    projectUuids: initialProjectUuids,
  }));

  useEffect(() => {
    const onMount = async () => {
      try {
        const projectProvider = await createTransactionProjectProvider();
        const projects = await projectProvider.getProjects();
        const projectCollection = new DataCollection<TransactionProject>();
        projectCollection.setCollection(
          new Collection(
            [allProjectsOption].concat(projects.items),
            projects.count,
            projects.totalCount
          )
        );
        setProjects(projectCollection);
      } catch (error) {
        if (error instanceof Error) {
          await handleError(error);
        }
      }
    };
    (async () => {
      await onMount();
    })();
  }, []);

  const applyFilter = (nextFilter: Filter): void => {
    setFilter({
      ...nextFilter,
      transactionDirection:
        nextFilter.transactionDirection === TransactionDirection.ALL
          ? null
          : nextFilter.transactionDirection,
    });
  };

  const loadData = async (page: number, perPage: number, filter: Filter) => {
    setLoading(true);
    try {
      const provider = await createTransactionProvider();
      const transactions = await provider.getList(page, perPage, filter);
      const dataCollection = new DataCollection<TransactionModel>();
      dataCollection.setCollection(transactions);
      setTransactions(dataCollection);
    } catch (err: any) {
      await errorCallback(err);
    } finally {
      setLoading(false);
    }
  };

  const isEmpty = transactions.items.length === 0;

  return (
    <Section>
      <Container>
        <AccountNotReadyWarningSection />
        <Pagination
          onLoad={loadData}
          count={transactions.count}
          totalCount={transactions.totalCount}
          filterObject={filter}
          initialChangeFilter={(value: Partial<Filter>) => setFilter({ ...filter, ...value })}
        >
          <div className="transactions">
            <div className="transactions__toolbar">
              <TransactionsPartialFilter
                filter={filter}
                onChange={applyFilter}
                projects={projects.items}
              />
            </div>
            <Pagination.Content loading={loading || transactionsTranslations.loading}>
              <div className="transactions__desktop">
                <DataTable
                  variant="surface"
                  columns={TABLE_COLUMNS}
                  className="transactions__table"
                >
                  <DataTable.Head>
                    <DataTable.HeadCell>Проект</DataTable.HeadCell>
                    <DataTable.HeadCell>Дата</DataTable.HeadCell>
                    <DataTable.HeadCell>Сумма</DataTable.HeadCell>
                    <DataTable.HeadCell>Тип транзакции</DataTable.HeadCell>
                    <DataTable.HeadCell>Тип счета</DataTable.HeadCell>
                    <DataTable.HeadCell>Пояснение</DataTable.HeadCell>
                  </DataTable.Head>
                  <DataTable.Body>
                    {isEmpty ? (
                      <DataTable.Empty>Транзакции не найдены</DataTable.Empty>
                    ) : (
                      transactions.items.map((transaction) => (
                        <TransactionTableRow transaction={transaction} key={transaction.uuid} />
                      ))
                    )}
                  </DataTable.Body>
                </DataTable>
              </div>
              <div className="transactions__mobile">
                {isEmpty ? (
                  <div className="transactions__empty">Транзакции не найдены</div>
                ) : (
                  transactions.items.map((transaction) => (
                    <TransactionMobileCard transaction={transaction} key={transaction.uuid} />
                  ))
                )}
              </div>
            </Pagination.Content>
          </div>
          <Pagination.Paginator />
        </Pagination>
      </Container>
    </Section>
  );
};

export default Transactions;
